/**
 * Clinical Assessment Panel
 * 
 * Enlarged, high-contrast clinical views for:
 * - Pain Assessment (embedded in visit notes)
 * - Symptom Assessment (integrated)
 * - Visit Frequency (large display)
 * 
 * Per Tier 2 Clinical Safety requirements:
 * - Large-font, high-contrast clinical views
 * - Embedded directly in visit documentation
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Slider } from "./ui/slider";
import { Progress } from "./ui/progress";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import {
  Activity, Heart, Calendar, AlertTriangle, CheckCircle, TrendingUp,
  TrendingDown, Clock, ThermometerSun, Pill, Save, Mic
} from "lucide-react";
import { toast } from "sonner";

// Pain level color mapping
const getPainColor = (level) => {
  if (level <= 3) return { bg: "bg-green-500", text: "text-green-500", label: "Mild" };
  if (level <= 6) return { bg: "bg-amber-500", text: "text-amber-500", label: "Moderate" };
  return { bg: "bg-red-500", text: "text-red-500", label: "Severe" };
};

// Symptom categories with clinical importance
const SYMPTOM_CATEGORIES = [
  { id: "pain", label: "Pain", icon: Activity, color: "red", critical: true },
  { id: "dyspnea", label: "Dyspnea", icon: Activity, color: "blue", critical: true },
  { id: "nausea", label: "Nausea/Vomiting", icon: ThermometerSun, color: "yellow", critical: false },
  { id: "anxiety", label: "Anxiety", icon: Heart, color: "purple", critical: false },
  { id: "fatigue", label: "Fatigue", icon: TrendingDown, color: "gray", critical: false },
  { id: "constipation", label: "Constipation", icon: Clock, color: "orange", critical: false },
  { id: "anorexia", label: "Poor Appetite", icon: Pill, color: "amber", critical: false },
  { id: "insomnia", label: "Sleep Issues", icon: Clock, color: "slate", critical: false },
  { id: "confusion", label: "Confusion", icon: AlertTriangle, color: "pink", critical: true },
  { id: "edema", label: "Edema", icon: Activity, color: "cyan", critical: false },
];

const PAIN_LOCATIONS = [
  "Head", "Neck", "Chest", "Back", "Abdomen", "Hip", "Arm", "Leg", "Generalized"
];

const PAIN_QUALITIES = [
  "Sharp", "Dull", "Burning", "Aching", "Stabbing", "Throbbing", "Cramping"
];

const INTERVENTIONS = [
  "Medication Given", "Repositioning", "Heat Applied", "Cold Applied", 
  "Massage", "Relaxation Techniques", "Distraction", "Family Support"
];

export function ClinicalAssessmentPanel({ 
  patientId, 
  patientName, 
  isDark = false,
  onSave,
  initialData = null,
  mode = "full" // "full", "pain", "symptom", "frequency"
}) {
  const [activeTab, setActiveTab] = useState(mode === "full" ? "pain" : mode);
  
  // Pain Assessment State
  const [painData, setPainData] = useState({
    pain_level: initialData?.pain_level || 0,
    pain_location: initialData?.pain_location || [],
    pain_quality: initialData?.pain_quality || [],
    onset: initialData?.onset || "",
    interventions: initialData?.interventions || [],
    effectiveness: initialData?.effectiveness || "",
    notes: initialData?.pain_notes || ""
  });

  // Symptom Assessment State
  const [symptomData, setSymptomData] = useState({
    active_symptoms: initialData?.active_symptoms || [],
    symptom_details: initialData?.symptom_details || {},
    overall_comfort: initialData?.overall_comfort || 7,
    notes: initialData?.symptom_notes || ""
  });

  // Visit Frequency State (typically read-only, from backend)
  const [visitFrequency, setVisitFrequency] = useState({
    benefit_period: "01/15/2026 - 04/14/2026",
    days_elapsed: 16,
    days_in_period: 90,
    disciplines: [
      { code: "SN", name: "Skilled Nursing", ordered: "2-3x/week", completed: 6, total: 36, compliance: 100, next: "01/28/2026" },
      { code: "MSW", name: "Social Worker", ordered: "1x/week PRN", completed: 2, total: 12, compliance: 100, next: "01/30/2026" },
      { code: "HHA", name: "Home Health Aide", ordered: "3x/week", completed: 5, total: 39, compliance: 95, next: "01/27/2026" },
      { code: "Chaplain", name: "Chaplain", ordered: "1x/week PRN", completed: 1, total: 12, compliance: 75, next: "Pending" },
      { code: "Vol", name: "Volunteer", ordered: "1x/week PRN", completed: 0, total: 12, compliance: 0, next: "02/01/2026" }
    ]
  });

  const toggleArrayItem = (arr, item) => {
    return arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item];
  };

  const toggleSymptom = (symptomId) => {
    if (symptomData.active_symptoms.includes(symptomId)) {
      setSymptomData({
        ...symptomData,
        active_symptoms: symptomData.active_symptoms.filter(s => s !== symptomId),
        symptom_details: { ...symptomData.symptom_details, [symptomId]: undefined }
      });
    } else {
      setSymptomData({
        ...symptomData,
        active_symptoms: [...symptomData.active_symptoms, symptomId],
        symptom_details: {
          ...symptomData.symptom_details,
          [symptomId]: { severity: 5, frequency: "intermittent", trend: "stable" }
        }
      });
    }
  };

  const updateSymptomDetail = (symptomId, field, value) => {
    setSymptomData({
      ...symptomData,
      symptom_details: {
        ...symptomData.symptom_details,
        [symptomId]: { ...symptomData.symptom_details[symptomId], [field]: value }
      }
    });
  };

  const handleSaveAll = () => {
    const assessmentData = {
      pain: painData,
      symptoms: symptomData,
      timestamp: new Date().toISOString()
    };
    toast.success("Clinical assessment saved");
    if (onSave) onSave(assessmentData);
  };

  const getComplianceColor = (compliance) => {
    if (compliance >= 90) return "text-green-500";
    if (compliance >= 75) return "text-amber-500";
    return "text-red-500";
  };

  const painColor = getPainColor(painData.pain_level);

  return (
    <div className={`space-y-4 ${isDark ? "text-white" : "text-gray-900"}`} data-testid="clinical-assessment-panel">
      {/* Patient Context Header */}
      <div className={`p-4 rounded-lg border-2 ${isDark ? "border-teal-700 bg-teal-900/20" : "border-teal-200 bg-teal-50"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDark ? "bg-teal-800" : "bg-teal-100"}`}>
              <Activity className={`w-6 h-6 ${isDark ? "text-teal-400" : "text-teal-600"}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold">{patientName || "Patient"}</h2>
              <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                Clinical Assessment • Embedded in Visit Note
              </p>
            </div>
          </div>
          <Button onClick={handleSaveAll} className="bg-teal-600 hover:bg-teal-700">
            <Save className="w-4 h-4 mr-2" />
            Save Assessment
          </Button>
        </div>
      </div>

      {/* Assessment Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`w-full justify-start ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
          <TabsTrigger value="pain" className="flex items-center gap-2 text-base px-6 py-3" data-testid="pain-tab">
            <Activity className="w-5 h-5" />
            Pain Assessment
            {painData.pain_level > 0 && (
              <Badge className={`ml-2 ${painColor.bg} text-white`}>{painData.pain_level}/10</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="symptom" className="flex items-center gap-2 text-base px-6 py-3" data-testid="symptom-tab">
            <ThermometerSun className="w-5 h-5" />
            Symptoms
            {symptomData.active_symptoms.length > 0 && (
              <Badge className="ml-2 bg-purple-500 text-white">{symptomData.active_symptoms.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="frequency" className="flex items-center gap-2 text-base px-6 py-3" data-testid="frequency-tab">
            <Calendar className="w-5 h-5" />
            Visit Frequency
          </TabsTrigger>
        </TabsList>

        {/* PAIN ASSESSMENT TAB - Enlarged */}
        <TabsContent value="pain" className="mt-4">
          <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-lg"}>
            <CardContent className="p-6">
              {/* Large Pain Scale */}
              <div className="mb-8">
                <Label className={`text-lg font-semibold mb-4 block ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                  Current Pain Level
                </Label>
                <div className="flex items-center gap-6">
                  <div className="flex-1">
                    <Slider 
                      value={[painData.pain_level]} 
                      onValueChange={([v]) => setPainData({...painData, pain_level: v})} 
                      max={10} 
                      step={1}
                      className="h-4"
                    />
                    <div className="flex justify-between mt-2 text-sm">
                      <span className="text-green-500 font-medium">0 - No Pain</span>
                      <span className="text-amber-500 font-medium">5 - Moderate</span>
                      <span className="text-red-500 font-medium">10 - Worst</span>
                    </div>
                  </div>
                  <div className={`w-32 h-32 rounded-2xl flex flex-col items-center justify-center ${painColor.bg} text-white shadow-lg`}>
                    <span className="text-5xl font-bold">{painData.pain_level}</span>
                    <span className="text-sm font-medium mt-1">{painColor.label}</span>
                  </div>
                </div>
              </div>

              <Separator className={isDark ? "bg-slate-700" : "bg-gray-200"} />

              {/* Pain Location - Large Buttons */}
              <div className="my-6">
                <Label className={`text-lg font-semibold mb-3 block ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                  Pain Location(s)
                </Label>
                <div className="flex flex-wrap gap-3">
                  {PAIN_LOCATIONS.map(loc => (
                    <Button 
                      key={loc}
                      variant={painData.pain_location.includes(loc) ? "default" : "outline"}
                      className={`text-base px-5 py-3 h-auto ${
                        painData.pain_location.includes(loc) 
                          ? "bg-teal-600 hover:bg-teal-700 text-white" 
                          : isDark ? "border-slate-600 hover:bg-slate-700" : "border-gray-300 hover:bg-gray-100"
                      }`}
                      onClick={() => setPainData({...painData, pain_location: toggleArrayItem(painData.pain_location, loc)})}
                    >
                      {loc}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Pain Quality - Large Buttons */}
              <div className="my-6">
                <Label className={`text-lg font-semibold mb-3 block ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                  Pain Quality
                </Label>
                <div className="flex flex-wrap gap-3">
                  {PAIN_QUALITIES.map(q => (
                    <Button 
                      key={q}
                      variant={painData.pain_quality.includes(q) ? "default" : "outline"}
                      className={`text-base px-5 py-3 h-auto ${
                        painData.pain_quality.includes(q) 
                          ? "bg-teal-600 hover:bg-teal-700 text-white" 
                          : isDark ? "border-slate-600 hover:bg-slate-700" : "border-gray-300 hover:bg-gray-100"
                      }`}
                      onClick={() => setPainData({...painData, pain_quality: toggleArrayItem(painData.pain_quality, q)})}
                    >
                      {q}
                    </Button>
                  ))}
                </div>
              </div>

              <Separator className={isDark ? "bg-slate-700" : "bg-gray-200"} />

              {/* Onset & Interventions */}
              <div className="grid md:grid-cols-2 gap-6 my-6">
                <div>
                  <Label className={`text-lg font-semibold mb-3 block ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                    Pain Onset
                  </Label>
                  <Select value={painData.onset} onValueChange={(v) => setPainData({...painData, onset: v})}>
                    <SelectTrigger className={`h-14 text-base ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300"}`}>
                      <SelectValue placeholder="When did pain start?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New (within 24h)</SelectItem>
                      <SelectItem value="recent">Recent (1-7 days)</SelectItem>
                      <SelectItem value="chronic">Chronic (ongoing)</SelectItem>
                      <SelectItem value="worsening">Worsening</SelectItem>
                      <SelectItem value="improving">Improving</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className={`text-lg font-semibold mb-3 block ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                    Intervention Effectiveness
                  </Label>
                  <RadioGroup 
                    value={painData.effectiveness} 
                    onValueChange={(v) => setPainData({...painData, effectiveness: v})}
                    className="flex gap-4"
                  >
                    <div className={`flex items-center p-3 rounded-lg border-2 cursor-pointer ${
                      painData.effectiveness === "effective" 
                        ? "border-green-500 bg-green-500/10" 
                        : isDark ? "border-slate-700" : "border-gray-200"
                    }`}>
                      <RadioGroupItem value="effective" id="eff" className="mr-2" />
                      <Label htmlFor="eff" className="cursor-pointer text-green-500 font-medium">Effective</Label>
                    </div>
                    <div className={`flex items-center p-3 rounded-lg border-2 cursor-pointer ${
                      painData.effectiveness === "partial" 
                        ? "border-amber-500 bg-amber-500/10" 
                        : isDark ? "border-slate-700" : "border-gray-200"
                    }`}>
                      <RadioGroupItem value="partial" id="part" className="mr-2" />
                      <Label htmlFor="part" className="cursor-pointer text-amber-500 font-medium">Partial</Label>
                    </div>
                    <div className={`flex items-center p-3 rounded-lg border-2 cursor-pointer ${
                      painData.effectiveness === "ineffective" 
                        ? "border-red-500 bg-red-500/10" 
                        : isDark ? "border-slate-700" : "border-gray-200"
                    }`}>
                      <RadioGroupItem value="ineffective" id="ineff" className="mr-2" />
                      <Label htmlFor="ineff" className="cursor-pointer text-red-500 font-medium">Ineffective</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>

              {/* Interventions Given */}
              <div className="my-6">
                <Label className={`text-lg font-semibold mb-3 block ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                  Interventions Given
                </Label>
                <div className="flex flex-wrap gap-3">
                  {INTERVENTIONS.map(i => (
                    <Button 
                      key={i}
                      variant={painData.interventions.includes(i) ? "default" : "outline"}
                      className={`text-base px-5 py-3 h-auto ${
                        painData.interventions.includes(i) 
                          ? "bg-blue-600 hover:bg-blue-700 text-white" 
                          : isDark ? "border-slate-600 hover:bg-slate-700" : "border-gray-300 hover:bg-gray-100"
                      }`}
                      onClick={() => setPainData({...painData, interventions: toggleArrayItem(painData.interventions, i)})}
                    >
                      {i}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <Label className={`text-lg font-semibold ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                    Pain Assessment Notes
                  </Label>
                  <Button variant="ghost" size="sm" className="text-teal-500">
                    <Mic className="w-4 h-4 mr-1" /> Dictate
                  </Button>
                </div>
                <Textarea 
                  value={painData.notes}
                  onChange={(e) => setPainData({...painData, notes: e.target.value})}
                  placeholder="Document pain details, patient response to interventions, and plan..."
                  rows={4}
                  className={`text-base ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300"}`}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SYMPTOM ASSESSMENT TAB - Enlarged */}
        <TabsContent value="symptom" className="mt-4">
          <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-lg"}>
            <CardContent className="p-6">
              {/* Symptom Selection Grid */}
              <div className="mb-6">
                <Label className={`text-lg font-semibold mb-4 block ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                  Active Symptoms (Select All That Apply)
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {SYMPTOM_CATEGORIES.map(symptom => (
                    <Button
                      key={symptom.id}
                      variant={symptomData.active_symptoms.includes(symptom.id) ? "default" : "outline"}
                      className={`h-auto py-4 px-4 flex flex-col items-center gap-2 ${
                        symptomData.active_symptoms.includes(symptom.id)
                          ? symptom.critical ? "bg-red-600 hover:bg-red-700" : "bg-teal-600 hover:bg-teal-700"
                          : isDark ? "border-slate-600 hover:bg-slate-700" : "border-gray-300 hover:bg-gray-100"
                      } ${symptom.critical && !symptomData.active_symptoms.includes(symptom.id) ? "border-red-300" : ""}`}
                      onClick={() => toggleSymptom(symptom.id)}
                    >
                      <symptom.icon className="w-5 h-5" />
                      <span className="text-sm font-medium text-center">{symptom.label}</span>
                      {symptom.critical && (
                        <Badge className="bg-red-500/20 text-red-300 text-[10px]">Critical</Badge>
                      )}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Active Symptom Details */}
              {symptomData.active_symptoms.length > 0 && (
                <>
                  <Separator className={isDark ? "bg-slate-700" : "bg-gray-200"} />
                  <div className="my-6">
                    <Label className={`text-lg font-semibold mb-4 block ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                      Symptom Details
                    </Label>
                    <div className="space-y-4">
                      {symptomData.active_symptoms.map(symptomId => {
                        const symptom = SYMPTOM_CATEGORIES.find(s => s.id === symptomId);
                        const detail = symptomData.symptom_details[symptomId] || { severity: 5, frequency: "intermittent", trend: "stable" };
                        const severityColor = detail.severity <= 3 ? "green" : detail.severity <= 6 ? "amber" : "red";
                        
                        return (
                          <div 
                            key={symptomId} 
                            className={`p-5 rounded-xl border-2 ${
                              symptom?.critical 
                                ? isDark ? "border-red-700 bg-red-900/10" : "border-red-200 bg-red-50"
                                : isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-gray-50"
                            }`}
                          >
                            <div className="flex items-center gap-3 mb-4">
                              <Badge className={symptom?.critical ? "bg-red-500" : "bg-teal-500"}>
                                {symptom?.label}
                              </Badge>
                              {symptom?.critical && (
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                              )}
                            </div>
                            
                            <div className="grid md:grid-cols-3 gap-6">
                              {/* Severity */}
                              <div>
                                <Label className={`mb-2 block ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                                  Severity (1-10)
                                </Label>
                                <div className="flex items-center gap-3">
                                  <Slider 
                                    value={[detail.severity]} 
                                    onValueChange={([v]) => updateSymptomDetail(symptomId, "severity", v)} 
                                    max={10} 
                                    step={1}
                                    className="flex-1"
                                  />
                                  <div className={`w-14 h-14 rounded-lg flex items-center justify-center bg-${severityColor}-500 text-white font-bold text-xl`}>
                                    {detail.severity}
                                  </div>
                                </div>
                              </div>

                              {/* Frequency */}
                              <div>
                                <Label className={`mb-2 block ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                                  Frequency
                                </Label>
                                <Select value={detail.frequency} onValueChange={(v) => updateSymptomDetail(symptomId, "frequency", v)}>
                                  <SelectTrigger className={`h-14 ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300"}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="constant">Constant</SelectItem>
                                    <SelectItem value="frequent">Frequent</SelectItem>
                                    <SelectItem value="intermittent">Intermittent</SelectItem>
                                    <SelectItem value="occasional">Occasional</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              {/* Trend */}
                              <div>
                                <Label className={`mb-2 block ${isDark ? "text-slate-300" : "text-gray-600"}`}>
                                  Trend
                                </Label>
                                <Select value={detail.trend} onValueChange={(v) => updateSymptomDetail(symptomId, "trend", v)}>
                                  <SelectTrigger className={`h-14 ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300"}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="worsening">
                                      <span className="flex items-center gap-2 text-red-500">
                                        <TrendingUp className="w-4 h-4" /> Worsening
                                      </span>
                                    </SelectItem>
                                    <SelectItem value="stable">
                                      <span className="flex items-center gap-2">Stable</span>
                                    </SelectItem>
                                    <SelectItem value="improving">
                                      <span className="flex items-center gap-2 text-green-500">
                                        <TrendingDown className="w-4 h-4" /> Improving
                                      </span>
                                    </SelectItem>
                                    <SelectItem value="new">New Onset</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {/* Overall Comfort */}
              <Separator className={isDark ? "bg-slate-700" : "bg-gray-200"} />
              <div className="my-6">
                <Label className={`text-lg font-semibold mb-4 block ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                  Overall Comfort Level
                </Label>
                <div className="flex items-center gap-6">
                  <div className="flex-1">
                    <Slider 
                      value={[symptomData.overall_comfort]} 
                      onValueChange={([v]) => setSymptomData({...symptomData, overall_comfort: v})} 
                      max={10} 
                      step={1}
                      className="h-4"
                    />
                    <div className="flex justify-between mt-2 text-sm">
                      <span className="text-red-500 font-medium">1 - Very Uncomfortable</span>
                      <span className="text-amber-500 font-medium">5 - Moderate</span>
                      <span className="text-green-500 font-medium">10 - Very Comfortable</span>
                    </div>
                  </div>
                  <div className={`w-24 h-24 rounded-2xl flex flex-col items-center justify-center ${
                    symptomData.overall_comfort >= 7 ? "bg-green-500" : symptomData.overall_comfort >= 4 ? "bg-amber-500" : "bg-red-500"
                  } text-white shadow-lg`}>
                    <span className="text-4xl font-bold">{symptomData.overall_comfort}</span>
                    <span className="text-xs font-medium">/10</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <Label className={`text-lg font-semibold ${isDark ? "text-slate-200" : "text-gray-700"}`}>
                    Symptom Assessment Notes
                  </Label>
                  <Button variant="ghost" size="sm" className="text-teal-500">
                    <Mic className="w-4 h-4 mr-1" /> Dictate
                  </Button>
                </div>
                <Textarea 
                  value={symptomData.notes}
                  onChange={(e) => setSymptomData({...symptomData, notes: e.target.value})}
                  placeholder="Document symptom observations, interventions, and recommendations..."
                  rows={4}
                  className={`text-base ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300"}`}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VISIT FREQUENCY TAB - Large Display */}
        <TabsContent value="frequency" className="mt-4">
          <Card className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-lg"}>
            <CardContent className="p-6">
              {/* Period Summary - Large */}
              <div className={`p-6 rounded-xl mb-6 ${isDark ? "bg-blue-900/20 border-2 border-blue-700" : "bg-blue-50 border-2 border-blue-200"}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className={`text-xl font-bold ${isDark ? "text-blue-300" : "text-blue-700"}`}>Current Benefit Period</h3>
                    <p className={`text-lg ${isDark ? "text-blue-400" : "text-blue-600"}`}>{visitFrequency.benefit_period}</p>
                  </div>
                  <div className={`text-center px-6 py-3 rounded-xl ${isDark ? "bg-blue-800" : "bg-blue-100"}`}>
                    <p className={`text-4xl font-bold ${isDark ? "text-blue-300" : "text-blue-700"}`}>
                      Day {visitFrequency.days_elapsed}
                    </p>
                    <p className={`text-sm ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                      of {visitFrequency.days_in_period}
                    </p>
                  </div>
                </div>
                <Progress value={(visitFrequency.days_elapsed / visitFrequency.days_in_period) * 100} className="h-4" />
              </div>

              {/* Discipline Grid - Large Cards */}
              <div className="space-y-4">
                {visitFrequency.disciplines.map((disc, idx) => (
                  <div 
                    key={idx} 
                    className={`p-5 rounded-xl border-2 ${
                      disc.compliance < 75 
                        ? isDark ? "border-red-700 bg-red-900/10" : "border-red-200 bg-red-50"
                        : isDark ? "border-slate-700 bg-slate-800/50" : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                          disc.compliance >= 90 ? "bg-green-500" : disc.compliance >= 75 ? "bg-amber-500" : "bg-red-500"
                        } text-white`}>
                          <span className="text-2xl font-bold">{disc.code}</span>
                        </div>
                        <div>
                          <h4 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>{disc.name}</h4>
                          <p className={`text-base ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                            Ordered: {disc.ordered}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-4xl font-bold ${getComplianceColor(disc.compliance)}`}>
                          {disc.compliance}%
                        </p>
                        <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>On Track</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-6 mb-4">
                      <div className={`text-center p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-white"}`}>
                        <p className={`text-3xl font-bold ${isDark ? "text-teal-400" : "text-teal-600"}`}>{disc.completed}</p>
                        <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>Completed</p>
                      </div>
                      <div className={`text-center p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-white"}`}>
                        <p className={`text-3xl font-bold ${isDark ? "text-blue-400" : "text-blue-600"}`}>{disc.total}</p>
                        <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>Total Ordered</p>
                      </div>
                      <div className={`text-center p-3 rounded-lg ${isDark ? "bg-slate-800" : "bg-white"}`}>
                        <p className={`text-lg font-bold ${disc.next === "Pending" ? "text-amber-500" : isDark ? "text-slate-300" : "text-gray-700"}`}>
                          {disc.next}
                        </p>
                        <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>Next Visit</p>
                      </div>
                    </div>

                    <Progress value={(disc.completed / disc.total) * 100} className="h-3" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default ClinicalAssessmentPanel;
